<template>
    <div class="recording-coach-page">
        <div class="row">
            <div class="col-md-6">
                <div class="panel panel-default">
                    <div class="panel-heading clearfix">
                        <strong>Trascrizione live</strong>
                        <span class="pull-right">
                            <span
                                class="label"
                                :class="statusLabelClass"
                                :title="echoConnected ? 'WebSocket attivo — aggiornamento in tempo reale' : 'WebSocket non connesso — polling ogni 5s'"
                            >
                                <i v-if="sessionStatus !== 'finalized' && echoConnected" class="fa fa-circle" style="font-size:8px;vertical-align:middle;margin-right:3px;"></i>
                                {{ statusLabel }}
                            </span>
                        </span>
                    </div>
                    <div class="panel-body recording-coach-page__transcript" ref="transcriptBox">
                        <p v-if="!hasTranscriptContent" class="text-muted">
                            In attesa del primo blocco trascritto (circa 1–2 minuti dopo l’inizio della registrazione)…
                        </p>
                        <div v-else-if="liveTranscriptSegments.length" class="recording-coach-page__transcript-segments">
                            <div
                                v-for="(chunk, cIdx) in liveTranscriptSegments"
                                :key="'chunk-' + (chunk.chunk_index != null ? chunk.chunk_index : cIdx)"
                                class="recording-coach-page__chunk"
                            >
                                <div v-if="chunk.time_label" class="recording-coach-page__chunk-time">
                                    {{ chunk.time_label }}
                                </div>
                                <div
                                    v-for="(line, lIdx) in (chunk.lines || [])"
                                    :key="'line-' + cIdx + '-' + lIdx"
                                    class="recording-coach-page__line"
                                >
                                    <span class="recording-coach-page__speaker">{{ line.speaker }}</span>
                                    <span class="recording-coach-page__line-text">{{ line.text }}</span>
                                </div>
                            </div>
                        </div>
                        <pre v-else class="recording-coach-page__transcript-text">{{ liveTranscript }}</pre>
                    </div>
                    <div class="panel-footer text-muted" style="font-size: 12px;">
                        <span v-if="liveTranscriptUpdatedAt">Aggiornato: {{ liveTranscriptUpdatedAt }}</span>
                        <span v-if="projectLabel" class="pull-right">Progetto: {{ projectLabel }}</span>
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="panel panel-default">
                    <div class="panel-heading">
                        <strong>Chat con il coach</strong>
                    </div>
                    <div class="panel-body recording-coach-page__chat-messages" ref="chatBox">
                        <div
                            v-for="(msg, idx) in messages"
                            :key="idx"
                            class="recording-coach-page__message"
                            :class="'recording-coach-page__message--' + msg.role"
                        >
                            <div v-html="formatMessage(msg.content)"></div>
                        </div>
                        <p v-if="isStreaming" class="text-muted"><i class="fa fa-spinner fa-spin"></i> Il coach sta rispondendo…</p>
                    </div>
                    <div class="panel-footer">
                        <form @submit.prevent="sendMessage">
                            <div class="input-group">
                                <textarea
                                    v-model="draft"
                                    class="form-control"
                                    rows="2"
                                    placeholder="Chiedi un riassunto, i prossimi passi, cosa è stato detto su… (Invio per inviare, Shift+Invio per andare a capo)"
                                    :disabled="isStreaming || !canChat"
                                    @keydown.enter.exact.prevent="sendMessage"
                                ></textarea>
                                <span class="input-group-btn" style="vertical-align: bottom;">
                                    <button
                                        type="submit"
                                        class="btn btn-primary"
                                        :disabled="isStreaming || !canChat || !draft.trim()"
                                    >
                                        Invia
                                    </button>
                                </span>
                            </div>
                        </form>
                        <p v-if="!canChat" class="text-muted" style="margin-top: 8px; margin-bottom: 0; font-size: 12px;">
                            Registrazione terminata. Puoi consultare la trascrizione; per la chat usa la riunione salvata.
                        </p>
                        <p v-if="meetingUrl" style="margin-top: 8px; margin-bottom: 0;">
                            <a :href="meetingUrl" class="btn btn-sm btn-success">Apri riunione in Brain</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RecordingCoachPage',
    props: {
        sessionUuid: { type: String, required: true },
        stateUrl: { type: String, required: true },
        messagesUrl: { type: String, required: true },
        chatUrl: { type: String, required: true },
    },
    data() {
        var boot = this.resolveInitialState();
        return {
            sessionStatus: boot.session_status || 'open',
            liveTranscript: boot.live_transcript || '',
            liveTranscriptSegments: this.normalizeTranscriptSegments(boot.live_transcript_segments),
            liveTranscriptUpdatedAt: boot.live_transcript_updated_at || null,
            projectLabel: boot.project_label || null,
            meetingUrl: boot.meeting_url || null,
            canChat: boot.can_chat !== false,
            messages: [],
            draft: '',
            isStreaming: false,
            pollTimer: null,
            echoChannel: null,
            echoConnected: false,
            lastTranscriptLen: (boot.live_transcript || '').length,
        };
    },
    computed: {
        statusLabel() {
            if (this.sessionStatus === 'finalized') return 'Registrazione terminata';
            return this.echoConnected ? 'In registrazione · live' : 'In registrazione';
        },
        statusLabelClass() {
            if (this.sessionStatus === 'finalized') return 'label-default';
            return this.echoConnected ? 'label-success' : 'label-warning';
        },
        hasTranscriptContent() {
            if (this.liveTranscriptSegments.length > 0) {
                return true;
            }
            return !!(this.liveTranscript && this.liveTranscript.trim());
        },
    },
    mounted() {
        this.loadMessages();
        this.subscribeEcho();
        // Polling di backup: veloce finché Echo non è connesso, poi ogni 15s
        this.pollTimer = setInterval(() => {
            if (!this.echoConnected || this.sessionStatus !== 'open') {
                this.refreshState();
            }
        }, 5000);
    },
    beforeDestroy() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
        }
        if (this.echoChannel && window.Echo) {
            try {
                window.Echo.leave('private-recording-session.' + this.sessionUuid);
            } catch (e) {
                // ignore
            }
        }
    },
    methods: {
        resolveInitialState() {
            if (typeof window !== 'undefined' && window.__brainRecordingCoachInitialState) {
                var state = window.__brainRecordingCoachInitialState;
                window.__brainRecordingCoachInitialState = null;
                return state;
            }
            return {};
        },
        normalizeTranscriptSegments(raw) {
            if (!Array.isArray(raw)) {
                return [];
            }
            return raw.map(function (chunk) {
                if (!chunk || typeof chunk !== 'object') {
                    return null;
                }
                var lines = Array.isArray(chunk.lines) ? chunk.lines : [];
                if (lines.length === 0 && chunk.text) {
                    var text = String(chunk.text);
                    lines = text.split('\n').map(function (line) {
                        var m = line.match(/^([^:]+):\s*(.+)$/);
                        if (m && !/^\[\d{1,2}:\d{2}\]$/.test(m[1].trim())) {
                            return { speaker: m[1].trim(), text: m[2].trim() };
                        }
                        return null;
                    }).filter(Boolean);
                }
                return {
                    chunk_index: chunk.chunk_index,
                    time_label: chunk.time_label || '',
                    lines: lines,
                    text: chunk.text || '',
                };
            }).filter(Boolean);
        },
        mergeChunkSegment(segment) {
            if (!segment || typeof segment !== 'object') {
                return;
            }
            var idx = segment.chunk_index;
            var list = this.liveTranscriptSegments.slice();
            var found = -1;
            for (var i = 0; i < list.length; i++) {
                if (list[i].chunk_index === idx) {
                    found = i;
                    break;
                }
            }
            var normalized = this.normalizeTranscriptSegments([segment])[0];
            if (!normalized) {
                return;
            }
            if (found >= 0) {
                list[found] = normalized;
            } else {
                list.push(normalized);
            }
            list.sort(function (a, b) {
                return (a.chunk_index || 0) - (b.chunk_index || 0);
            });
            this.liveTranscriptSegments = list;
        },
        formatMessage(raw) {
            if (!raw) { return ''; }
            var s = raw;

            // Se il contenuto non sembra HTML (nessun tag rilevante), converti markdown
            var hasHtmlTags = /<(strong|em|ul|ol|li|br|p|h[1-6]|b|i)\b/i.test(s);
            if (!hasHtmlTags) {
                // Blocchi di codice
                s = s.replace(/```[\s\S]*?```/g, function (m) {
                    return '<pre style="background:#f4f4f4;padding:6px 8px;border-radius:4px;font-size:12px;overflow-x:auto;">' + m.replace(/```/g, '').trim() + '</pre>';
                });
                // Intestazioni ### ## #
                s = s.replace(/^#{1,3} (.+)$/gm, function (_, t) { return '<strong>' + t + '</strong>'; });
                // Grassetto **text** o __text__
                s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
                s = s.replace(/__(.+?)__/g, '<strong>$1</strong>');
                // Corsivo *text* o _text_ (non doppio)
                s = s.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
                s = s.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');

                // Liste non ordinate (- * –)
                s = s.replace(/^[\-\*–•] (.+)$/gm, '<li>$1</li>');
                s = s.replace(/(<li>[\s\S]*?<\/li>)(\n(<li>[\s\S]*?<\/li>))*/g, function (m) {
                    return '<ul style="margin:4px 0 4px 16px;padding:0;">' + m + '</ul>';
                });

                // Liste ordinate 1. 2. …
                s = s.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
                s = s.replace(/(<li>[\s\S]*?<\/li>)(\n(<li>[\s\S]*?<\/li>))*/g, function (m) {
                    if (m.indexOf('<ul') === -1) {
                        return '<ol style="margin:4px 0 4px 16px;padding:0;">' + m + '</ol>';
                    }
                    return m;
                });

                // Paragrafi: righe vuote → separatore
                s = s.replace(/\n{2,}/g, '<br><br>');
                // Singoli a-capo
                s = s.replace(/\n/g, '<br>');
            }

            return s;
        },
        scrollTranscript() {
            var box = this.$refs.transcriptBox;
            if (box) {
                box.scrollTop = box.scrollHeight;
            }
        },
        scrollChat() {
            var box = this.$refs.chatBox;
            if (box) {
                box.scrollTop = box.scrollHeight;
            }
        },
        applyState(state) {
            if (!state) {
                return;
            }
            this.sessionStatus = state.session_status;
            this.liveTranscript = state.live_transcript || '';
            this.liveTranscriptSegments = this.normalizeTranscriptSegments(state.live_transcript_segments);
            this.liveTranscriptUpdatedAt = state.live_transcript_updated_at;
            this.projectLabel = state.project_label;
            this.meetingUrl = state.meeting_url;
            if (typeof state.can_chat === 'boolean') {
                this.canChat = state.can_chat;
            }
        },
        refreshState() {
            var self = this;
            fetch(this.stateUrl, {
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
            })
                .then(function (r) { return r.json(); })
                .then(function (data) {
                    self.applyState(data);
                    self.$nextTick(function () { self.scrollTranscript(); });
                })
                .catch(function () {});
        },
        loadMessages() {
            var self = this;
            fetch(this.messagesUrl, {
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
            })
                .then(function (r) { return r.json(); })
                .then(function (data) {
                    self.messages = (data.messages || []).map(function (m) {
                        return { role: m.role, content: m.content };
                    });
                    self.$nextTick(function () { self.scrollChat(); });
                })
                .catch(function () {});
        },
        subscribeEcho() {
            if (!window.Echo || typeof window.Echo.private !== 'function') {
                return;
            }
            var self = this;
            try {
                var channel = window.Echo.private('recording-session.' + this.sessionUuid);

                // Tenta di rilevare la connessione tramite il socket Pusher sottostante
                if (window.Echo.connector && window.Echo.connector.pusher) {
                    window.Echo.connector.pusher.connection.bind('connected', function () {
                        self.echoConnected = true;
                    });
                    window.Echo.connector.pusher.connection.bind('disconnected', function () {
                        self.echoConnected = false;
                    });
                    window.Echo.connector.pusher.connection.bind('unavailable', function () {
                        self.echoConnected = false;
                    });
                    // Considera già connesso se lo stato è già 'connected'
                    if (window.Echo.connector.pusher.connection.state === 'connected') {
                        self.echoConnected = true;
                    }
                }

                this.echoChannel = channel
                    .listen('.transcript.delta', function (e) {
                        if (e.chunk_segment) {
                            self.mergeChunkSegment(e.chunk_segment);
                            if (e.updated_at) {
                                self.liveTranscriptUpdatedAt = e.updated_at;
                            }
                            self.$nextTick(function () { self.scrollTranscript(); });
                            return;
                        }
                        self.refreshState();
                    })
                    .listen('.session.status', function (e) {
                        self.sessionStatus = e.status || self.sessionStatus;
                        if (e.meeting_url) { self.meetingUrl = e.meeting_url; }
                        self.refreshState();
                    });
            } catch (err) {
                // Echo non disponibile — il polling di backup copre il caso
                console.warn('RecordingCoach: Echo subscribe failed', err);
            }
        },
        sendMessage() {
            var text = this.draft.trim();
            if (!text || this.isStreaming || !this.canChat) {
                return;
            }
            var self = this;
            this.messages.push({ role: 'user', content: text });
            this.draft = '';
            this.isStreaming = true;
            this.$nextTick(function () { self.scrollChat(); });

            var assistantIdx = this.messages.length;
            this.messages.push({ role: 'assistant', content: '' });

            var csrf = document.querySelector('meta[name="csrf-token"]');
            var token = csrf ? csrf.getAttribute('content') : '';

            fetch(this.chatUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    'X-CSRF-TOKEN': token,
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
                body: JSON.stringify({ message: text }),
            }).then(function (response) {
                if (!response.ok || !response.body) {
                    throw new Error('Richiesta non riuscita');
                }
                var reader = response.body.getReader();
                var decoder = new TextDecoder();
                var buffer = '';

                function pump() {
                    return reader.read().then(function (result) {
                        if (result.done) {
                            self.isStreaming = false;
                            self.$nextTick(function () { self.scrollChat(); });
                            return;
                        }
                        buffer += decoder.decode(result.value, { stream: true });
                        var lines = buffer.split('\n');
                        buffer = lines.pop() || '';
                        lines.forEach(function (line) {
                            if (line.indexOf('data: ') !== 0) {
                                return;
                            }
                            try {
                                var payload = JSON.parse(line.slice(6));
                                if (payload.error) {
                                    self.messages[assistantIdx].content += '<br><span class="text-danger">' + payload.error + '</span>';
                                } else if (payload.choices && payload.choices[0] && payload.choices[0].delta && payload.choices[0].delta.content) {
                                    self.messages[assistantIdx].content += payload.choices[0].delta.content;
                                }
                            } catch (err) {
                                // ignore parse errors
                            }
                        });
                        self.$nextTick(function () { self.scrollChat(); });
                        return pump();
                    });
                }
                return pump();
            }).catch(function (err) {
                self.isStreaming = false;
                self.messages[assistantIdx].content = '<span class="text-danger">Errore: ' + (err.message || 'invio fallito') + '</span>';
            });
        },
    },
};
</script>

<style scoped>
.recording-coach-page__transcript {
    max-height: 420px;
    overflow-y: auto;
}
.recording-coach-page__transcript-text {
    white-space: pre-wrap;
    word-break: break-word;
    font-family: inherit;
    font-size: 13px;
    border: none;
    background: transparent;
    margin: 0;
    padding: 0;
}
.recording-coach-page__transcript-segments {
    font-size: 13px;
}
.recording-coach-page__chunk {
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
}
.recording-coach-page__chunk:last-child {
    border-bottom: none;
    margin-bottom: 0;
}
.recording-coach-page__chunk-time {
    font-size: 11px;
    font-weight: 600;
    color: #888;
    margin-bottom: 6px;
}
.recording-coach-page__line {
    margin-bottom: 8px;
    line-height: 1.45;
}
.recording-coach-page__speaker {
    display: inline-block;
    font-weight: 600;
    color: #2c5282;
    margin-right: 6px;
    white-space: nowrap;
}
.recording-coach-page__line-text {
    color: #333;
}
.recording-coach-page__chat-messages {
    max-height: 360px;
    overflow-y: auto;
}
.recording-coach-page__message {
    margin-bottom: 12px;
    padding: 8px 10px;
    border-radius: 4px;
    font-size: 13px;
}
.recording-coach-page__message--user {
    background: #f0f4f8;
    margin-left: 24px;
}
.recording-coach-page__message--assistant {
    background: #fff9e6;
    margin-right: 24px;
}
</style>
